// 旅前プランナーの API (#1237)。プラン生成/カタログ/保存はここに追随。
// 配置ルールは index.ts のヘッダ参照。
import { apiFetch } from '../../services/api/_client'

// ---- 旅前プランナー (#1237) 型定義 ----
export interface PlanArea {
  id: string
  name: string
  nameEn: string
  centerLat: number
  centerLng: number
  radiusM: number
}

export interface PlanSpotLink {
  type: string
  url: string
  label?: string
  isPrimary?: boolean
}

export interface PlanSpot {
  id: string
  areaId: string
  name: string
  nameEn: string
  description: string
  descriptionEn: string
  genre: string
  latitude: number | null
  longitude: number | null
  openTime: string
  closeTime: string
  closedDays: string[]
  hoursNote: string
  stayMinutes: number
  /** #1491 体験が紐づく場所は所要時間に幅がある (選ぶもので 90分〜3時間) */
  stayMinutesMin?: number | null
  stayMinutesMax?: number | null
  /** #1493 プランが確定して提案する体験（滞在時間・費用はこれと同じ体験の値） */
  experience?: {
    id: string
    name: string
    nameEn: string
    priceJpy: number
    durationMinutes?: number | null
    transportIncluded?: boolean
  } | null
  personaTags: string[]
  budgetTier: 'low' | 'mid' | 'high' | null
  priceNote: string
  /** 概算費用の合算用 (大人1人・税込の下限値。0 = 無料) */
  priceFrom: number
  imageUrl: string
  googleMapsUrl: string
  links: PlanSpotLink[]
  linkedStoreId: string | null
  /** #1263: PAY 登録店舗由来の仮想候補 (id は store::{storeId})。バッジ表示用 */
  isStoreCandidate?: boolean
  /** #1253/#1263 Phase 2: 実データ推薦の理由 (ログイン + 集計済みエリアのみ付く)。
   * compatriot = 同じ国籍 / same_age = 同じ年代 / lens = 明示選択したセグメント */
  recoReason?: 'your_taste' | 'compatriot' | 'same_age' | 'popular' | 'lens' | null
  /** #1320: 手動スポット (custom::) の入力実体。保存 payload と編集フォームのプリフィルに使う。
   * mapsUrl = Google Maps リンク (サーバが座標抽出) / officialUrl = 公式サイト等 */
  manual?: { mapsUrl?: string; officialUrl?: string; kind?: PlanCustomKind }
  displayOrder: number
}

export interface PlanSlot {
  slotKey: string
  startTime: string
  timeOfDay: string
  swapped: boolean
  /** 滞在時間の個別上書き (分)。空き挿入の「短縮滞在」用。未指定 = spot の目安 */
  stayMinutes?: number
  /** #1265: 一言メモ (集合場所・予約時刻など)。保存時にサーバへ引き継ぐ */
  memo?: string
  spot: PlanSpot
}

export interface PlanDay {
  day: number
  slots: PlanSlot[]
  /** #1318: 終日フリー(宿でゆっくり)の日。スポットを置かず空ける */
  isRest?: boolean
}

export interface GeneratedPlan {
  templateId: string
  variant: 'classic' | 'hidden' | string
  personaTag: string
  title: string
  titleEn: string
  description: string
  nights: number
  days: PlanDay[]
}

export interface PlanAreaOptions {
  area: PlanArea
  nightsOptions: number[]
  personaTags: string[]
  /** #1272 ②層: テンプレなし = アンカー + PAY 店舗からの自動生成エリア (β) */
  isAuto?: boolean
}

export interface PlanOptions {
  areas: PlanAreaOptions[]
  budgetTiers: string[]
}

export interface PlanGenerateResult {
  area: PlanArea
  classic: GeneratedPlan | null
  hidden: GeneratedPlan | null
}

export interface SavedPlanSlot {
  slotKey: string
  startTime: string
  timeOfDay: string
  spotId: string
  snapshot: {
    name: string
    nameEn: string
    genre: string
    /** #1241 follow-up で追加 — それ以前の保存プランには無い (optional) */
    description?: string
    descriptionEn?: string
    latitude: number | null
    longitude: number | null
    openTime: string
    closeTime: string
    closedDays: string[]
    hoursNote?: string
    stayMinutes: number
    /** #1491 体験が紐づく場所は所要時間に幅がある (選ぶもので 90分〜3時間) */
    stayMinutesMin?: number | null
    stayMinutesMax?: number | null
    /** #1493 プランが確定して提案する体験（滞在時間・費用はこれと同じ体験の値） */
    experience?: {
      id: string
      name: string
      nameEn: string
      priceJpy: number
      durationMinutes?: number | null
      transportIncluded?: boolean
    } | null
    priceNote: string
    priceFrom?: number
    imageUrl: string
    googleMapsUrl: string
    links?: PlanSpotLink[]
    linkedStoreId: string | null
  }
  memo: string | null
  /** #1320: カタログ外の手動スポット (spotId が custom::)。PATCH で往復させる実体 */
  custom?: PlanCustomSpot | null
}

/** #1278: エリアで開催 (中|予定) のイベント (既存スタンプラリー資産を流用) */
export interface PlanAreaEvent {
  id: string
  name: string
  /** 'qr' (スタンプラリー) / 'ar' (AR宝探し) / 'external' */
  type: string
  /** 開催開始。未来 = 開催前 (「旅行期間中に開催」バッジ)。日程指定の取得でのみ未来があり得る */
  startsAt?: string
  endsAt: string
  regionId: string
  /** 開催場所 (region 中心) — 「プランに追加」時の slot 座標に使う */
  venueLat?: number
  venueLng?: number
}

/** #1320: 手動スポットの種別。hotel = 宿 (チェックイン/アウト表示・滞在なし)、
 * station = 駅・乗換 (既定 5 分) */
export type PlanCustomKind = 'place' | 'hotel' | 'station'

/** #1320: 手動スポットの入力実体 (PH2 のカタログ昇格集計にも使う)。
 * mapsUrl = Google Maps リンク。サーバが座標を抽出して地図・移動時間の対象にする */
export interface PlanCustomSpot {
  name: string
  stayMinutes?: number | null
  url?: string
  mapsUrl?: string
  kind?: PlanCustomKind
}

export interface SavedPlanDay {
  day: number
  slots: SavedPlanSlot[]
  /** #1318: 終日予定なしの日 (宿でゆっくり/仕事/友人等) */
  isRest?: boolean
}

export interface SavedPlan {
  id: string
  areaId: string
  areaName: string
  areaNameEn: string
  title: string
  titleEn: string
  nights: number
  personas: string[]
  budgetTier: 'low' | 'mid' | 'high' | null
  variantOrigin: 'classic' | 'hidden' | 'custom'
  startDate: string | null
  status: 'active' | 'archived'
  days: SavedPlanDay[]
  createdAt: string
  updatedAt: string
}

/** #1243: 共有リンクの公開ビュー。PII (memberId / startDate / slots[].memo) は
 * サーバ側 serializer が落とすため型にも存在しない。日程は Day N の相対表示 */
export interface SharedPlanSlot {
  slotKey: string
  startTime: string
  timeOfDay: string
  snapshot: Partial<SavedPlanSlot['snapshot']>
}

export interface SharedPlanDay {
  day: number
  isRest?: boolean
  slots: SharedPlanSlot[]
}

export interface SharedPlan {
  shareCode: string
  areaId: string
  areaName: string
  areaNameEn: string
  title: string
  titleEn: string
  nights: number
  personas: string[]
  budgetTier: 'low' | 'mid' | 'high' | null
  days: SharedPlanDay[]
}

/** #1243: trip (全体しおり) の共有公開ビュー。実旅行日・wishes (個人メモ) は
 * サーバ側で除外済み。座標は公開施設 (空港・観光エリア) のもので、区間の
 * 移動手段・移動費の概算表示に使う。stops[].plan は紐づく子プランの共有ビュー */
export interface SharedTripGateway {
  name: string
  nameEn: string
  kind: string
  lat: number | null
  lng: number | null
}

export interface SharedTripStop {
  name: string
  nameEn: string
  lat: number | null
  lng: number | null
  nights: number
  departTime: string | null
  hotelCheckIn: string | null
  hotelCheckOut: string | null
  hotelNights: TripHotelNight[]
  restDays: number[]
  plan: SharedPlan | null
}

export interface SharedTrip {
  shareCode: string
  title: string
  titleEn: string
  origin: SharedTripGateway
  destination: SharedTripGateway
  roundTrip: boolean
  nights: number
  originDepartTime: string | null
  goalReturnTime: string | null
  stops: SharedTripStop[]
}

export type SharedTarget =
  | { kind: 'plan'; plan: SharedPlan }
  | { kind: 'trip'; trip: SharedTrip }

/** #1242: admin カタログ管理 (管理者のみ)。下書き・非公開込みの運用ビュー */
export interface AdminPlanSpot {
  id: string
  areaId: string
  name: string
  nameEn: string
  description: string
  descriptionEn: string
  genre: string
  latitude: number | null
  longitude: number | null
  mapsUrl?: string
  openTime: string
  closeTime: string
  closedDays: string[]
  hoursNote: string
  stayMinutes: number
  personaTags: string[]
  budgetTier: 'low' | 'mid' | 'high' | null
  priceNote: string
  priceFrom: number
  imageUrl: string
  sourceUrl: string
  linkedStoreId: string | null
  links?: { type: string; url: string; isPrimary?: boolean }[]
  displayOrder: number
  isActive: boolean
  /** AI ドラフト (未承認)。isActive=true で公開すると外れる */
  aiDraft?: boolean
  /** AI 検索グラウンディングの出典ドメイン — 承認前の裏取り先 */
  aiSources?: string[]
}

export interface AdminPlanSpotBody {
  name: string
  nameEn: string
  description: string
  descriptionEn: string
  genre: string
  latitude: number | null
  longitude: number | null
  /** Google Maps リンク貼付 → サーバが座標を抽出 (#1328 対策)。lat/lng より優先 */
  mapsUrl?: string | null
  openTime: string
  closeTime: string
  closedDays: string[]
  hoursNote: string
  stayMinutes: number
  personaTags: string[]
  budgetTier: 'low' | 'mid' | 'high' | null
  priceNote: string
  priceFrom: number
  imageUrl: string
  sourceUrl: string
  linkedStoreId: string | null
  primaryLinkUrl: string
  displayOrder: number
  isActive: boolean
}

export interface AdminPlanTemplateSlot {
  slotKey: string
  startTime: string
  timeOfDay: string
  spotId: string
  alternativeSpotIds: string[]
}

export interface AdminPlanTemplate {
  id: string
  areaId: string
  title: string
  titleEn: string
  description: string
  nights: number
  variant: 'classic' | 'hidden'
  personaTag: string
  displayOrder: number
  isActive: boolean
  days: { day: number; slots: AdminPlanTemplateSlot[] }[]
}

export type AdminPlanTemplateBody = Omit<AdminPlanTemplate, 'id' | 'areaId'>

/** #1244: 誘客レポート (管理者のみ)。個人を特定できる値は含まない集計のみ */
export interface PlanAttractSpotRow {
  spotId: string
  name: string
  nameEn: string
  genre: string
  count: number
}

export interface PlanAttractReport {
  areaId: string
  areaName: string
  areaNameEn: string
  days: number
  funnel: {
    visitors: number
    generated: number
    spotSelects: number
    savedPlans: number
    savers: number
    sharesIssued: number
    shareViewsTotal: number
    payingVisitors: number
    payments: number
    paymentAmountJpy: number
  }
  topSpots: PlanAttractSpotRow[]
  personaRanking: {
    persona: string
    selectCount: number
    topSpots: PlanAttractSpotRow[]
  }[]
}

export interface PlanSaveRequestBody {
  areaId: string
  title: string
  titleEn: string
  nights: number
  personas: string[]
  budgetTier: string | null
  variantOrigin: 'classic' | 'hidden' | 'custom'
  startDate: string | null
  days: {
    day: number
    /** #1318: 終日予定なしの日 */
    isRest?: boolean
    slots: {
      slotKey: string
      startTime: string
      timeOfDay: string
      spotId: string
      /** 滞在時間の個別上書き (分)。snapshot.stayMinutes に反映される */
      stayMinutes?: number
      /** #1265: 一言メモ (最大500字はサーバ側で検証) */
      memo?: string | null
      /** #1320: spotId が custom:: の手動スポットの実体 */
      custom?: PlanCustomSpot
    }[]
  }[]
}

export interface PlanRecommendationItem {
  spot: PlanSpot
  distanceM: number
  walkMinutes: number
  /** 優先順位: 同好の実データ (#1253) > 同国籍/同年代の実績 > タグ一致 > 全体人気 > 近さ */
  reason: 'your_taste' | 'compatriot' | 'same_age' | 'persona_match' | 'popular' | 'nearby'
}

export interface PlanRecommendationResult {
  day: number
  inPeriod: boolean
  gapMinutes: number | null
  nextSlot: { slotKey: string; startTime: string; name: string; nameEn: string } | null
  items: PlanRecommendationItem[]
}

// ---- 親 trip (全体行程) の保存 (#1273 Phase C) ----

export interface TripGatewaySnapshot {
  /** #1322: preset id (TRIP_GATEWAYS)。編集モードでの復元用 (旧 doc は無し) */
  id?: string | null
  name: string
  nameEn: string
  kind: string
  lat: number | null
  lng: number | null
}

/** #1322: 滞在地ごとの手入力「行きたい所」(サーバ保存) */
export interface TripWish {
  id: string
  name: string
  url: string
}

/** #1243 FB: 泊ごとの宿。night = 滞在 N 泊目 (1 始まり) */
export interface TripHotelNight {
  night: number
  name: string
  priceYen: number | null
  /** 予約ページ / Google マップ等 (サーバで http(s) のみ許可) */
  url: string | null
  checkIn: string | null
  checkOut: string | null
}

/** #1280: 1 滞在地に持てる候補プラン (パターン) の上限 (サーバと同値) */
export const MAX_PLAN_CANDIDATES = 4

export interface SavedTripStop {
  id: string
  name: string
  nameEn: string
  lat: number | null
  lng: number | null
  areaId: string | null
  isAuto: boolean
  nights: number
  /** 紐づいた子エリアプランの id (未作成なら null)。#1280: 候補の中の「確定」1 本 */
  planId: string | null
  /** #1280: 候補プラン (パターン) の id 一覧。planId を必ず含む (旧データはサーバが導出) */
  candidatePlanIds: string[]
  /** #1322: しおり編集内容 (サーバが真実) */
  departTime: string | null
  hotelCheckIn: string | null
  hotelCheckOut: string | null
  /** #1243 FB: 泊ごとの宿 (1泊目と2泊目で別の宿があり得る)。night 昇順 */
  hotelNights: TripHotelNight[]
  wishes: TripWish[]
  restDays: number[]
  /** 全体しおり取得時のみ埋まる子プランの実体 (削除済みなら null) */
  childPlan: SavedPlan | null
  /** #1280: 全体しおり取得時のみ埋まる候補プランの実体 (candidatePlanIds 順) */
  candidatePlans: SavedPlan[]
}

export interface SavedTrip {
  id: string
  title: string
  titleEn: string
  origin: TripGatewaySnapshot
  destination: TripGatewaySnapshot
  roundTrip: boolean
  /** #1629: true = 滞在地の並びは手動指定 (編集時に自動整列で上書きしない)。旧 doc は無し */
  orderLocked?: boolean
  stops: SavedTripStop[]
  nights: number
  startDate: string | null
  endDate: string | null
  /** #1322: 出発地の出発時刻 / 帰りの便・終電の出発時刻 */
  originDepartTime: string | null
  goalReturnTime: string | null
  status: 'active' | 'archived'
  createdAt: string
  updatedAt: string
}

export interface TripSaveRequestBody {
  title: string
  titleEn: string
  origin: TripGatewaySnapshot
  destination: TripGatewaySnapshot
  roundTrip: boolean
  /** #1629: 手動並び順フラグ */
  orderLocked?: boolean
  stops: {
    id: string
    name: string
    nameEn: string
    lat: number | null
    lng: number | null
    areaId?: string | null
    isAuto?: boolean
    nights: number
    planId?: string | null
    /** #1322: しおり編集内容 (保存時に下書きオーバーレイを同梱) */
    departTime?: string | null
    hotelCheckIn?: string | null
    hotelCheckOut?: string | null
    wishes?: TripWish[]
    restDays?: number[]
  }[]
  nights: number
  startDate: string | null
  endDate: string | null
  originDepartTime?: string | null
  goalReturnTime?: string | null
}

/** #1322: stop 単位のしおり編集 (しおり画面の debounce PATCH が送る) */
export interface TripStopOverlayPatch {
  stopId: string
  departTime?: string | null
  hotelCheckIn?: string | null
  hotelCheckOut?: string | null
  hotelNights?: TripHotelNight[]
  wishes?: TripWish[]
  restDays?: number[]
}

/** #1322: PATCH /trips/{id} の body。構造更新 (stops) は wizard の編集確定のみが送る */
export type TripUpdateBody = Partial<
  Pick<
    SavedTrip,
    'title' | 'titleEn' | 'startDate' | 'endDate' | 'status' | 'originDepartTime' | 'goalReturnTime' | 'roundTrip' | 'nights' | 'orderLocked'
  >
> & {
  origin?: TripGatewaySnapshot
  destination?: TripGatewaySnapshot
  stops?: TripSaveRequestBody['stops']
  stopOverlays?: TripStopOverlayPatch[]
  /** 子プランの紐づけ (#1280 で候補化)。mode: select = 候補に加えて確定 (既定) /
   * add = 候補追加のみ / remove = 候補から外す (確定を外すと残り先頭へフォールバック) */
  link?: { stopId: string; planId: string | null; mode?: 'select' | 'add' | 'remove' }
}

/** パーソナルカタログの棚 (#1274)。segment 棚は axis/value でラベルを組む */
export interface PlanShelf {
  key: 'your_taste' | 'segment' | 'popular'
  axis: 'nat' | 'age' | 'gender' | null
  value: string | null
  spots: PlanSpot[]
}

export interface PlanSelectionLog {
  action: 'generate' | 'add' | 'remove' | 'swap' | 'save'
  deviceId?: string
  areaId?: string
  spotId?: string
  slotKey?: string
  personas?: string[]
  budgetTier?: string
  variant?: string
}

export const planApi = {
  // =====================================================================
  // 旅前プランナー (#1237) — 顧客側。閲覧/生成はゲスト可 (apiFetch が
  // ログイン時のみ Authorization を付与し、サーバは optional auth で受ける)
  // =====================================================================
  getPlanOptions: async (): Promise<PlanOptions> => {
    const res = await apiFetch('/plan/options')
    return res.json()
  },
  /** #1278: 各エリアで開催 (中|予定) のイベント (スタンプラリー等)。
   * イベントが無いエリアはレスポンスに含まれない (あれば出すだけで良い)。
   * dateRange (旅行日程、YYYY-MM-DD) を渡すと「その期間と開催が交差する」判定に
   * なる — 旅程は未来の日付で組むので、渡せる画面では必ず渡す (Phase 2) */
  getPlanAreaEvents: async (
    areaIds: string[],
    dateRange?: { from: string; to: string },
  ): Promise<{ events: Record<string, PlanAreaEvent[]> }> => {
    const range = dateRange
      ? `&dateFrom=${encodeURIComponent(dateRange.from)}&dateTo=${encodeURIComponent(dateRange.to)}`
      : ''
    const res = await apiFetch(
      `/plan/area-events?areaIds=${encodeURIComponent(areaIds.join(','))}${range}`,
    )
    return res.json()
  },
  generatePlans: async (params: {
    areaId: string
    nights: number
    personas?: string[]
    budgetTier?: string | null
  }): Promise<PlanGenerateResult> => {
    const res = await apiFetch('/plan/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        areaId: params.areaId,
        nights: params.nights,
        personas: params.personas ?? [],
        budgetTier: params.budgetTier ?? null,
      }),
    })
    return res.json()
  },
  getPlanCatalog: async (params: {
    areaId: string
    genre?: string
    persona?: string
    q?: string
    /** #1263 Phase 2: プラン作成中のペルソナ。ログイン時のみサーバが順位付けに使う */
    personas?: string[]
    /** #1274: 'axis:value' のカンマ区切り — セグメントのランキングに切替。
     * 複数指定は掛け合わせ (例 'nat:US,gender:female' = 両方に人気な順) */
    lens?: string
  }): Promise<{
    area: PlanArea
    spots: PlanSpot[]
    /** 実績が閾値を超えたセグメントのみ (ゲストは空)。一覧の絞り込みチップに使う */
    lensOptions: { axis: 'nat' | 'age' | 'gender'; value: string }[]
  }> => {
    const qs = new URLSearchParams({ areaId: params.areaId })
    if (params.genre) qs.set('genre', params.genre)
    if (params.persona) qs.set('persona', params.persona)
    if (params.q) qs.set('q', params.q)
    if (params.personas?.length) qs.set('personas', params.personas.join(','))
    if (params.lens) qs.set('lens', params.lens)
    const res = await apiFetch(`/plan/catalog?${qs}`)
    return res.json()
  },
  getPlanDiscover: async (params: { areaId: string }): Promise<{ shelves: PlanShelf[] }> => {
    // #1274 パーソナルカタログ (要ログイン)。未集計エリアは棚ゼロ →
    // 呼び側が通常カタログ表示へフォールバックする
    const res = await apiFetch(
      `/plan/discover?areaId=${encodeURIComponent(params.areaId)}`,
    )
    return res.json()
  },
  savePlan: async (body: PlanSaveRequestBody): Promise<{ plan: SavedPlan }> => {
    const res = await apiFetch('/plan/plans', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json()
  },
  getMyPlans: async (): Promise<{ plans: SavedPlan[] }> => {
    const res = await apiFetch('/plan/plans')
    return res.json()
  },
  getSavedPlan: async (planId: string): Promise<{ plan: SavedPlan }> => {
    const res = await apiFetch(`/plan/plans/${encodeURIComponent(planId)}`)
    return res.json()
  },
  updateSavedPlan: async (
    planId: string,
    patch: Partial<Pick<SavedPlan, 'title' | 'titleEn' | 'startDate' | 'status'>> & {
      /** 空き挿入等の日程編集。サーバが snapshot を再構築する */
      days?: PlanSaveRequestBody['days']
    },
  ): Promise<{ plan: SavedPlan }> => {
    const res = await apiFetch(`/plan/plans/${encodeURIComponent(planId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    return res.json()
  },
  deleteSavedPlan: async (planId: string): Promise<void> => {
    await apiFetch(`/plan/plans/${encodeURIComponent(planId)}`, { method: 'DELETE' })
  },
  // #1243: 共有コード発行 (所有者のみ)。既発行なら同じコードが返る
  createPlanShare: async (planId: string): Promise<{ shareCode: string }> => {
    const res = await apiFetch(`/plan/plans/${encodeURIComponent(planId)}/share`, {
      method: 'POST',
    })
    return res.json()
  },
  createTripShare: async (tripId: string): Promise<{ shareCode: string }> => {
    const res = await apiFetch(`/plan/trips/${encodeURIComponent(tripId)}/share`, {
      method: 'POST',
    })
    return res.json()
  },
  // #1243: 共有リンクの公開 lookup (未ログイン可)。plan / trip どちらかが返る
  getShared: async (shareCode: string): Promise<SharedTarget> => {
    const res = await apiFetch(`/plan/shared/${encodeURIComponent(shareCode)}`)
    return res.json()
  },
  // #1242: admin カタログ管理 (管理者のみ)
  adminListPlanSpots: async (areaId: string): Promise<{ spots: AdminPlanSpot[] }> => {
    const res = await apiFetch(`/plan/admin/catalog/spots?areaId=${encodeURIComponent(areaId)}`)
    return res.json()
  },
  adminSavePlanSpot: async (
    areaId: string,
    spotId: string | null,
    body: AdminPlanSpotBody,
  ): Promise<{ spot: AdminPlanSpot }> => {
    const path = spotId
      ? `/plan/admin/catalog/spots/${encodeURIComponent(spotId)}`
      : '/plan/admin/catalog/spots'
    const res = await apiFetch(`${path}?areaId=${encodeURIComponent(areaId)}`, {
      method: spotId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json()
  },
  adminDeletePlanSpot: async (spotId: string): Promise<void> => {
    await apiFetch(`/plan/admin/catalog/spots/${encodeURIComponent(spotId)}`, {
      method: 'DELETE',
    })
  },
  adminListPlanTemplates: async (
    areaId: string,
  ): Promise<{ templates: AdminPlanTemplate[] }> => {
    const res = await apiFetch(
      `/plan/admin/catalog/templates?areaId=${encodeURIComponent(areaId)}`,
    )
    return res.json()
  },
  adminSavePlanTemplate: async (
    areaId: string,
    tplId: string | null,
    body: AdminPlanTemplateBody,
  ): Promise<{ template: AdminPlanTemplate }> => {
    const path = tplId
      ? `/plan/admin/catalog/templates/${encodeURIComponent(tplId)}`
      : '/plan/admin/catalog/templates'
    const res = await apiFetch(`${path}?areaId=${encodeURIComponent(areaId)}`, {
      method: tplId ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json()
  },
  adminDeletePlanTemplate: async (tplId: string): Promise<void> => {
    await apiFetch(`/plan/admin/catalog/templates/${encodeURIComponent(tplId)}`, {
      method: 'DELETE',
    })
  },
  adminAiDraftPlanSpot: async (
    areaId: string,
    name: string,
    url: string,
  ): Promise<{ spot: AdminPlanSpot }> => {
    const res = await apiFetch('/plan/admin/catalog/spots/ai-draft', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ areaId, name, url }),
    })
    return res.json()
  },
  adminAiTranslatePlanSpot: async (
    spotId: string,
  ): Promise<{ nameEn: string; descriptionEn: string }> => {
    const res = await apiFetch(
      `/plan/admin/catalog/spots/${encodeURIComponent(spotId)}/ai-translate`,
      { method: 'POST' },
    )
    return res.json()
  },
  // #1244: 自治体・DMO 向け誘客レポート (管理者のみ)
  getPlanAttractReport: async (
    areaId: string,
    days: number,
  ): Promise<PlanAttractReport> => {
    const q = new URLSearchParams({ areaId, days: String(days) })
    const res = await apiFetch(`/plan/admin/attract-report?${q}`)
    return res.json()
  },
  getPlanRecommendations: async (
    planId: string,
    params: { lat: number; lng: number; at?: string },
  ): Promise<PlanRecommendationResult> => {
    const qs = new URLSearchParams({ lat: String(params.lat), lng: String(params.lng) })
    if (params.at) qs.set('at', params.at)
    const res = await apiFetch(
      `/plan/plans/${encodeURIComponent(planId)}/recommendations?${qs}`,
    )
    return res.json()
  },
  // ---- 親 trip (全体行程) の保存 (#1273 Phase C、要ログイン) ----
  saveTrip: async (body: TripSaveRequestBody): Promise<{ trip: SavedTrip }> => {
    const res = await apiFetch('/plan/trips', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return res.json()
  },
  getMyTrips: async (): Promise<{ trips: SavedTrip[] }> => {
    const res = await apiFetch('/plan/trips')
    return res.json()
  },
  getTrip: async (tripId: string): Promise<{ trip: SavedTrip }> => {
    const res = await apiFetch(`/plan/trips/${encodeURIComponent(tripId)}`)
    return res.json()
  },
  updateTrip: async (
    tripId: string,
    patch: TripUpdateBody,
  ): Promise<{ trip: SavedTrip }> => {
    const res = await apiFetch(`/plan/trips/${encodeURIComponent(tripId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    return res.json()
  },
  deleteTrip: async (tripId: string): Promise<void> => {
    await apiFetch(`/plan/trips/${encodeURIComponent(tripId)}`, { method: 'DELETE' })
  },
  logPlanSelection: async (log: PlanSelectionLog): Promise<void> => {
    // fire-and-forget: 選好ログの失敗で UX を止めない (サーバ側も best-effort)
    try {
      await apiFetch('/plan/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(log),
      })
    } catch {
      // 意図的に握りつぶす
    }
  },
}
